import { gSSP } from "app/blitz-server"
import { GetServerSideProps } from "next"
import { invoke } from "@blitzjs/rpc"
import * as Sentry from "@sentry/nextjs"
import { BlitzPage } from "@blitzjs/next"
import Layout from "app/core/layouts/Layout"
import { Suspense } from "react"
import { SymbolIcon } from "@radix-ui/react-icons"

import { Report, ReportProps } from "app/features/dashboard"
import { COUNTY_REPORT_TYPE } from "app/features/report-finder/constants"

import getCounty from "app/counties/queries/getCounty"
import getBlockgroupByCounty from "app/blockgroups/queries/getBlockgroupByCounty"
import getBlockgroupByMunicipalities from "app/blockgroups/queries/getBlockgroupByMunicipalities"
import { arrayUnique } from "app/utils"

const CountyInsightsPage: BlitzPage<ReportProps> = ({
  reportData,
}: {
  reportData: ReportProps["reportData"]
}) => {
  return <Report reportData={reportData} />
}

const Loading = () => {
  return (
    <div className="flex items-center justify-center gap-x-2 p-2 py-4 text-gray-500">
      <SymbolIcon />
      Loading…
    </div>
  )
}

CountyInsightsPage.getLayout = (page) => (
  <Layout title={page.props.pageTitle}>
    <Suspense fallback={<Loading />}>{page}</Suspense>
  </Layout>
)

export const getServerSideProps: GetServerSideProps = gSSP(async ({ params }) => {
  const slug = params?.slug ?? ""
  const county = await Sentry.startSpan({ name: "getCounty", op: "db" }, () =>
    invoke(getCounty, { slug: slug })
  )
  const blockgroups = await Sentry.startSpan(
    { name: "getBlockgroupByCounty", op: "db" },
    async (span) => {
      const result = await invoke(getBlockgroupByCounty, { countySlug: county.slug })
      span.setAttribute("report.blockgroup.count", result?.length ?? 0)
      return result
    }
  )

  const municipalitySlugs =
    blockgroups && blockgroups.length > 0
      ? arrayUnique(
          blockgroups.map((bg) => bg.municipality_slug).filter((slug) => slug !== null) as string[]
        )
      : undefined

  const allBlockgroups = await Sentry.startSpan(
    { name: "getBlockgroupByMunicipalities", op: "db" },
    async (span) => {
      const result = await invoke(getBlockgroupByMunicipalities, {
        municipalitySlugs: municipalitySlugs || [],
      })
      span.setAttribute("municipality.blockgroup.count", result?.length ?? 0)
      span.setAttribute("municipality.slug.count", municipalitySlugs?.length ?? 0)
      return result
    }
  )
  // Municipality block groups outside the report area, needed for correct composite-locality scores
  const extraBlockgroups = Sentry.startSpan(
    { name: "dedupExtraBlockgroups", op: "function" },
    (span) => {
      const reportBlockgroupIds = new Set(blockgroups?.map((b) => b.id))
      const extra =
        blockgroups && allBlockgroups
          ? allBlockgroups.filter((a) => !reportBlockgroupIds.has(a.id))
          : []
      span.setAttribute("report.blockgroup.count", blockgroups?.length ?? 0)
      span.setAttribute("municipality.blockgroup.count", allBlockgroups?.length ?? 0)
      span.setAttribute("extra.blockgroup.count", extra.length)
      return extra
    }
  )
  const reportData: ReportProps["reportData"] = {
    meta: {
      title: `${county.name}, ${county.state}`,
      shortTitle: county.name,
      type: COUNTY_REPORT_TYPE,
      slug: `${slug}`,
    },
    blockgroups: blockgroups || [],
    extraBlockgroups: extraBlockgroups.length > 0 ? extraBlockgroups : undefined,
  }

  const pageTitle = `${county.name}, ${county.state} Location Insights`

  return { props: { reportData, pageTitle } }
})

export default CountyInsightsPage
